import { ZAlert, ZButton, ZTypography } from "@atoms";
import { ZModal } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import {
	AddClientRequest,
	Client,
	PendingOpportunitySharedWithPartner,
	ZomentumRoutes
} from "@/Contracts";
import { useComponentDidUpdate } from "@/Hooks";
import { Col, Row, Skeleton } from "antd";
import React, { useState } from "react";
import { IViewOpportunityRequestModalProps } from "./interface";
import ZEntityDetailsTable from "./ZEntityDetailsTable";
import { IEntityDetailsTableRecord } from "./ZEntityDetailsTable/interface";
import { customDateFormatter } from "@/Utils";
import useDatePattern from "@/Hooks/useDatePattern";
import ZClientConflictModal from "./ZClientConflictModal";
import "./index.less";
import { getShippingAddressRowText } from "@/Utils/common";
import ZRejectRequestModal from "./ZRejectRequestModal";
import { useHistory } from "react-router-dom";

const ZViewOpportunityRequestModal: React.FC<
	IViewOpportunityRequestModalProps
> = ({
	visible,
	onCancelViewOpportunityRequestModal,
	pendingOpportunityId,
	getPendingOpportunity,
	checkConflictForPendingOpportunity,
	acceptPendingOpportunity,
	rejectPendingOpportunity,
	createClient,
	disableTransition
}) => {
	const history = useHistory();
	const datePattern = useDatePattern();
	const [isLoading, setIsLoading] = useState(false);
	const [pendingOpportunity, setPendingOpportunity] =
		useState<PendingOpportunitySharedWithPartner | null>(null);
	const [conflictedClient, setConflictedClient] = useState<Client | null>(
		null
	);
	const [showClientConflictModal, setShowClientConflictModal] =
		useState(false);
	const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
	const fetchPendingOpportunity = async () => {
		try {
			setIsLoading(true);
			const pendingOpportunity = await getPendingOpportunity(
				pendingOpportunityId
			);
			setPendingOpportunity(pendingOpportunity);
			if (!pendingOpportunity.existing_client_company_id) {
				const checkConflictForPendingOpportunityResponse =
					await checkConflictForPendingOpportunity(
						pendingOpportunityId
					);
				if (!!checkConflictForPendingOpportunityResponse.data.length) {
					const conflictedClient =
						checkConflictForPendingOpportunityResponse.data[0];
					setConflictedClient(conflictedClient);
				}
			}
		} catch (error) {
			onCancelViewOpportunityRequestModal();
		} finally {
			setIsLoading(false);
		}
	};
	useComponentDidUpdate(() => {
		if (visible) {
			fetchPendingOpportunity();
		}
	}, [visible]);
	const getVendorDetailsRecords = () => {
		const partnerDetailsRecords: IEntityDetailsTableRecord[] = [
			{
				field: "Vendor Name",
				value: pendingOpportunity?.vendor?.name ?? ""
			},
			{
				field: "Tier with Vendor",
				value: pendingOpportunity?.vendor?.tier_with_vendor?.name ?? ""
			}
		];
		return partnerDetailsRecords;
	};
	const getClientDetailsRecords = () => {
		const clientDetailsRecords: IEntityDetailsTableRecord[] = [
			{
				field: "Client Name",
				value: pendingOpportunity?.vendor_client_company?.name ?? ""
			}
		];
		if (pendingOpportunity?.vendor_client_company?.billing_address) {
			clientDetailsRecords.push({
				field: "Billing Address",
				value: getShippingAddressRowText(
					pendingOpportunity.vendor_client_company.billing_address
				)
			});
		}
		return clientDetailsRecords;
	};
	const getOpportunityDetailsRecords = () => {
		const opportunityDetailsRecords: IEntityDetailsTableRecord[] = [
			{
				field: "Opportunity Name",
				value: pendingOpportunity?.name ?? ""
			},
			{
				field: "Opportunity Type",
				value: pendingOpportunity?.vendor_opportunity_type ?? ""
			},
			{
				field: "Opportunity Stage of Vendor",
				value: pendingOpportunity?.opportunity_stage ?? ""
			},
			{
				field: "Estimated Value",
				value: `${pendingOpportunity?.estimated_value ?? ""}`
			},
			{
				field: "Estimated Close Date",
				value: pendingOpportunity?.estimated_close_date
					? customDateFormatter(
							pendingOpportunity.estimated_close_date as Date,
							datePattern
					  )
					: "--"
			}
		];
		return opportunityDetailsRecords;
	};
	const onAcceptRequest = async () => {
		try {
			setIsLoading(true);
			if (!!pendingOpportunity?.duplicate_opportunity_id) {
				const redirectToDuplicateOpportunity = () => {
					history.push(
						ZomentumRoutes.OpportunityDetail.replace(
							":id",
							pendingOpportunity.duplicate_opportunity_id ?? ""
						)
					);
					duplicateOpportunityModal.destroy();
				};
				const duplicateOpportunityModal = ZModal.warning({
					title: "Opportunity with same name already exists",
					content: (
						<>
							An opportunity with the same name{" "}
							<ZTypography.ZLink
								onClick={redirectToDuplicateOpportunity}
							>
								{pendingOpportunity.name}
							</ZTypography.ZLink>{" "}
							already exists in your CRM. Please update the name
							of the existing opportunity{" "}
							<ZTypography.ZLink
								onClick={redirectToDuplicateOpportunity}
							>
								{pendingOpportunity.name}
							</ZTypography.ZLink>{" "}
							to proceed further.
						</>
					),
					width: 600
				});
			} else if (conflictedClient) {
				setShowClientConflictModal(true);
			} else {
				if (!!pendingOpportunity?.existing_client_company_id) {
					await acceptPendingOpportunity(
						pendingOpportunity.existing_client_company_id
					);
				} else {
					const createClientRequest = new AddClientRequest();
					createClientRequest.name =
						pendingOpportunity?.vendor_client_company?.name ?? "";
					createClientRequest.fax =
						pendingOpportunity?.vendor_client_company?.fax ?? "";
					createClientRequest.website =
						pendingOpportunity?.vendor_client_company?.website ??
						"";
					createClientRequest.linked_in_url =
						pendingOpportunity?.vendor_client_company
							?.linked_in_url ?? null;
					createClientRequest.facebook_url =
						pendingOpportunity?.vendor_client_company
							?.facebook_url ?? null;
					createClientRequest.twitter_url =
						pendingOpportunity?.vendor_client_company
							?.twitter_url ?? null;
					createClientRequest.company_type =
						pendingOpportunity?.vendor_client_company
							?.company_type ??
						pendingOpportunity?.vendor_client_company
							?.company_types?.[0] ??
						null;
					createClientRequest.billing_address =
						pendingOpportunity?.vendor_client_company
							?.billing_address ?? null;
					const newClient = await createClient(createClientRequest);
					await acceptPendingOpportunity(newClient.id);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};
	const onRejectRequest = async (rejectReason: string) => {
		try {
			setIsLoading(true);
			await rejectPendingOpportunity(pendingOpportunityId, rejectReason);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
			setShowRejectRequestModal(false);
		}
	};

	useComponentDidUpdate(() => {
		if (!visible) {
			setShowClientConflictModal(false);
			setConflictedClient(null);
		}
	}, [visible]);
	return (
		<>
			<ZModal
				className="z-view-opportunity-request-modal"
				visible={visible}
				size="xl"
				onCancel={onCancelViewOpportunityRequestModal}
				title={
					isLoading ? (
						<span className="text-gray-1">Loading</span>
					) : (
						pendingOpportunity?.name
					)
				}
				transitionName={disableTransition ? "" : undefined}
				mask={disableTransition ? false : true}
				okText="Accept Request"
				cancelText="Reject Request"
				cancelButtonProps={{
					danger: true
				}}
				footer={[
					<ZButton
						key="back-lint"
						type="link"
						onClick={onCancelViewOpportunityRequestModal}
						iconName={EIcons.ARROWLEFT}
						loading={isLoading}
						className="p-0"
					>
						Back
					</ZButton>,
					<div key="empty-div" className="flex-1" />,
					<ZButton
						key="reject-request-button"
						onClick={() => setShowRejectRequestModal(true)}
						className="border-red-6 bg-gray-1 hover:bg-red-6"
						danger
						loading={isLoading}
					>
						Reject Request
					</ZButton>,
					<ZButton
						key="accept-request-button"
						onClick={onAcceptRequest}
						type="primary"
						loading={isLoading}
					>
						Accept Request
					</ZButton>
				]}
			>
				<Skeleton loading={isLoading}>
					{conflictedClient && (
						<ZAlert
							className="mb-4"
							showIcon={true}
							type="warning"
							message={`Another client with the same name ${conflictedClient.name} already exists in your CRM.`}
						/>
					)}
					<Row gutter={16}>
						<Col span={12}>
							<ZEntityDetailsTable
								title="Vendor Details"
								records={getVendorDetailsRecords()}
								className="mb-4"
								labelWidth={200}
							/>
							<ZEntityDetailsTable
								title="Client Details"
								records={getClientDetailsRecords()}
								labelWidth={200}
							/>
						</Col>
						<Col span={12}>
							<ZEntityDetailsTable
								title="Opportunity Details"
								records={getOpportunityDetailsRecords()}
							/>
						</Col>
					</Row>
				</Skeleton>
			</ZModal>
			{conflictedClient && pendingOpportunity && (
				<ZClientConflictModal
					visible={showClientConflictModal}
					conflictedClient={conflictedClient}
					pendingOpportunity={pendingOpportunity}
					onCancel={() => setShowClientConflictModal(false)}
					acceptPendingOpportunity={acceptPendingOpportunity}
					createClient={createClient}
				/>
			)}
			{pendingOpportunity && (
				<ZRejectRequestModal
					visible={showRejectRequestModal}
					onCancel={() => setShowRejectRequestModal(false)}
					pendingOpportunity={pendingOpportunity}
					onReject={onRejectRequest}
					isLoading={isLoading}
				/>
			)}
		</>
	);
};

export default ZViewOpportunityRequestModal;
